/**
 * Typed family-app data hooks — thin wrappers over the GENERATED Orval client functions (the only
 * sanctioned API access path). The generated functions now carry real response types (from the
 * OpenAPI response schemas), so these hooks reuse those return types directly, attach query params,
 * and carry an `Idempotency-Key` on offline-safe writes. All HTTP still flows through the generated
 * function + the shared mutator.
 *
 * TanStack Query keys are stable + structured so optimistic features can target them precisely.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { idempotent, type QueryOpts } from '../internal/query-utils';

import {
  tasksControllerDayV1,
  tasksControllerMissionsV1,
  tasksControllerFinishedMissionsV1,
  tasksControllerWeekV1,
  tasksControllerApprovalsV1,
  tasksControllerCompletionTrendV1,
  occurrencesControllerGetV1,
  choreActionsControllerCompleteV1,
  choreActionsControllerUncompleteV1,
  choreActionsControllerSkipV1,
  occurrencesControllerBackfillV1,
  occurrencesControllerRedoV1,
  occurrencesControllerApproveV1,
  occurrencesControllerRejectV1,
  photoProofControllerUploadV1,
  photoProofControllerPhotoCompleteV1,
  membersControllerListV1,
  membersControllerGetV1,
  membersControllerUpdateProfileV1,
  balancesControllerBalanceV1,
  balancesControllerBalanceTrendV1,
  balancesControllerIncomeForecastV1,
  balancesControllerLedgerV1,
  balancesControllerWaysToEarnV1,
  balancesControllerStreaksV1,
  learningControllerGoalStatusV1,
  learningControllerGoalTrendV1,
  rewardsControllerListV1,
  rewardsControllerGetV1,
  redemptionsControllerRequestV1,
  redemptionsControllerListV1,
  redemptionsControllerCancelV1,
  redemptionsControllerCollectV1,
  rewardsControllerFavoriteV1,
  rewardsControllerUnfavoriteV1,
  rewardGoalControllerGetV1,
  rewardGoalControllerSetV1,
  rewardGoalControllerClearV1,
  savingsControllerCreateV1,
  savingsControllerListForChildV1,
  savingsControllerUpdateV1,
  savingsControllerArchiveV1,
  savingsControllerCompleteV1,
  savingsControllerDepositV1,
  savingsControllerWithdrawV1,
  holdsControllerListForChildV1,
  holdsControllerListActiveV1,
  habitsControllerTodayV1,
  habitsControllerCheckInV1,
  habitsControllerUncheckV1,
  habitsControllerExcuseV1,
  habitsControllerUnexcuseV1,
  habitsControllerHistoryV1,
  playControllerStateV1,
  playControllerChooseHeroV1,
  playControllerUnlockHeroV1,
  playControllerUnlockPetV1,
  playControllerEquipPetV1,
  playControllerUnlockBackgroundV1,
  playControllerEquipBackgroundV1,
  playControllerRenameHeroV1,
  cosmeticsControllerCatalogV1,
  cosmeticsControllerEquippedV1,
  cosmeticsControllerPurchaseV1,
  cosmeticsControllerEquipV1,
  cosmeticsControllerUnequipV1,
  questsControllerActiveV1,
  questsControllerListV1,
  questsControllerCreateV1,
  questsControllerArchiveV1,
  questsControllerCompleteObjectiveV1,
  questsControllerPitchInV1,
} from '../generated/endpoints';
import { apiFetch, axiosInstance } from '../mutator';
import type {
  ApprovalItemResponse,
  ApproveOccurrenceDto,
  BalancesControllerLedgerV1200,
  BalancesControllerLedgerV1Params,
  CheckInHabitDto,
  CheckInHabitResponse,
  ExcuseHabitDayResponse,
  UnexcuseHabitDayResponse,
  ChildBalanceResponse,
  ChildBalanceTrendResponse,
  IncomeForecastResponse,
  RewardGoalResponse,
  ChildStreaksResponse,
  CompletionTrendResponse,
  WaysToEarnResponse,
  GoalStatusResponse,
  GoalTrendResponse,
  HabitHistoryResponse,
  CosmeticCatalogResponse,
  CosmeticItemDto,
  CreateRedemptionDto,
  CreateSavingsGoalDto,
  CreateSavingsGoalResponse,
  DayTasksResponse,
  EquippedCosmeticsResponse,
  HoldResponse,
  HouseholdHoldResponse,
  MemberResponse,
  MembersControllerListV1200,
  MissionsResponse,
  TemplateResponse,
  OccurrenceResponse,
  PlayStateResponse,
  ChooseHeroDto,
  ChooseHeroResponse,
  UnlockHeroDto,
  UnlockHeroResponse,
  UnlockPetDto,
  UnlockPetResponse,
  EquipPetDto,
  EquipPetResponse,
  UnlockBackgroundDto,
  UnlockBackgroundResponse,
  EquipBackgroundDto,
  EquipBackgroundResponse,
  RenameHeroDto,
  RenameHeroResponse,
  PurchaseCosmeticResponse,
  RedemptionsControllerListV1200,
  RejectOccurrenceDto,
  RequestRedemptionResponse,
  RewardResponse,
  SavingsGoalResponse,
  SavingsMovementDto,
  UpdateSavingsGoalDto,
  TodayHabitsResponse,
  UncheckHabitDto,
  UncheckHabitResponse,
  UpdateMemberProfileDto,
  UploadPhotoProofResponse,
  WeekAgendaResponse,
  ActiveQuestsResponse,
  CreateQuestDto,
  CreateQuestResponse,
  QuestsControllerListV1200,
} from '../generated/models';

/**
 * Generate a fresh idempotency key for an offline-safe write. The PWA outbox (Phase 7) reuses the
 * key on replay so a retried mutation never double-applies; for now each invocation mints one.
 */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // Deterministic fallback for environments without WebCrypto (kept dependency-free).
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// --- query keys (structured + stable) ---

export const familyKeys = {
  members: () => ['members'] as const,
  member: (memberId: string) => ['members', memberId] as const,
  day: (assigneeId: string, date: string) => ['tasks', 'day', assigneeId, date] as const,
  // An assignee's open anytime "missions" (ADR-0141) — date-independent, so keyed by assignee only. A
  // mission complete/undo invalidates this so the Missions lane reconciles.
  missions: (assigneeId: string) => ['tasks', 'missions', assigneeId] as const,
  // An assignee's FINISHED anytime "missions" (ADR-0141) — the read-only history shelf, keyed by
  // assignee only. Under the same `['tasks','missions']` prefix so a mission complete/undo reconciles
  // both the open lane and the history at once.
  missionsFinished: (assigneeId: string) => ['tasks', 'missions', assigneeId, 'finished'] as const,
  week: (assigneeId: string, weekStart: string) =>
    ['tasks', 'week', assigneeId, weekStart] as const,
  // Prefix of EVERY cached week-agenda window for an assignee. A task write (complete/skip/redo/
  // backfill/approve) knows whose occurrence changed but not which week is on screen, so it reconciles
  // the calendar across all cached weeks for that assignee (prefix match) — not just one weekStart.
  weeks: (assigneeId: string) => ['tasks', 'week', assigneeId] as const,
  // Root prefix of EVERY task-projection read (day/week/missions). Under ADR-0142 the calendar is a
  // pure projection over ACTIVE templates, so a TEMPLATE write (create/edit/pause/resume/archive/
  // restore/delete) changes any assignee's day/week/missions immediately — but only knows the
  // templateId, not the assignees, so it invalidates this coarse root instead of per-assignee keys.
  tasksRoot: () => ['tasks'] as const,
  approvals: () => ['tasks', 'approvals'] as const,
  occurrence: (occurrenceId: string) => ['tasks', 'occurrence', occurrenceId] as const,
  balance: (childId: string) => ['economy', 'balance', childId] as const,
  // A child's daily coin-balance trend over a window — the Wallet sparkline + the guardian per-child
  // view. Keyed by the window so distinct ranges cache independently.
  balanceTrend: (childId: string, from: string, to: string) =>
    ['economy', 'balance-trend', childId, from, to] as const,
  streaks: (childId: string) => ['economy', 'streaks', childId] as const,
  // A child's projected coin income (ADR-0090): realistic pace + rules ceiling. Drives the guardian
  // reward-pricing hint + the admin per-child forecast. Self/child keyed like the other balance reads.
  incomeForecast: (childId: string) => ['economy', 'income-forecast', childId] as const,
  // An assignee's chore completion-rate trend over a window (the "showing up" trend + parent oversight).
  completionTrend: (assigneeId: string, from: string, to: string) =>
    ['tasks', 'completion-trend', assigneeId, from, to] as const,
  // A child's transparent "Ways to earn coins" (ADR-0080): reward amounts + streak/mastery bonuses with
  // live progress. `"self"` = the signed-in kid (the API self-scopes a child to their own).
  waysToEarn: (childId: string) => ['economy', 'ways-to-earn', childId] as const,
  // A child's active learning-goal status (ADR-0080): target + reward + today's sessions. Same self/child
  // keying — a kid reads their own, a guardian may preview a specific child.
  goalStatus: (childId: string) => ['learning', 'goal-status', childId] as const,
  // A child's daily-goal adherence trend over a window (the "did they hit their goal each day" strip).
  goalTrend: (childId: string, from: string, to: string) =>
    ['learning', 'goal-trend', childId, from, to] as const,
  ledger: (childId: string, currency: string | null) =>
    ['economy', 'ledger', childId, currency ?? 'all'] as const,
  rewards: (forChildId: string | null) => ['economy', 'rewards', forChildId ?? 'self'] as const,
  reward: (rewardId: string, forChildId: string | null) =>
    ['economy', 'reward', rewardId, forChildId ?? 'self'] as const,
  rewardGoal: (childId: string) => ['economy', 'reward-goal', childId] as const,
  // Prefix over BOTH savings reads (`"live"` board + `"all"` history) for one child. Invalidate this so a
  // deposit / withdraw / archive / complete refreshes whichever variant a surface is subscribed to — the
  // full key (below) only matches one variant, so a board that reads `"all"` misses a `"live"` invalidation.
  savingsRoot: (childId: string) => ['economy', 'savings', childId] as const,
  savings: (childId: string, includeArchived?: boolean) =>
    ['economy', 'savings', childId, includeArchived ? 'all' : 'live'] as const,
  holds: (childId: string) => ['economy', 'holds', childId] as const,
  // Every active hold in the household (the global "what's paused" banner).
  householdHolds: () => ['economy', 'holds', 'household'] as const,
  // The signed-in kid's own redemption requests (the API self-scopes a child to their own).
  redemptions: () => ['economy', 'redemptions', 'self'] as const,
  // The signed-in child's habits for today (the API self-scopes a child to their own day view). A
  // guardian viewing ONE child passes that child's id so the two never share a cache slot; the bare
  // (self) form stays the `["habits","today"]` prefix so a `habitsToday()` invalidation reconciles both.
  habitsToday: (childId?: string | null) =>
    childId ? (['habits', 'today', childId] as const) : (['habits', 'today'] as const),
  // One habit's day-by-day history (done/excused) over a window — the calendar + consistency strip.
  habitHistory: (habitId: string, from: string, to: string) =>
    ['habits', 'history', habitId, from, to] as const,
  // Prefix for ALL windows of one habit's history — prefix-invalidate after a backfill/excuse so every
  // cached window (consistency strip etc.) reconciles, regardless of its [from,to].
  habitHistoryPrefix: (habitId: string) => ['habits', 'history', habitId] as const,
  // A child's progression (level/XP + companion). `"self"` = the signed-in child (the API defaults
  // to the caller when `childId` is omitted); a guardian's view of a child is keyed by that child's
  // id so the two never share a cache slot.
  playState: (childId?: string | null) => ['play', 'state', childId ?? 'self'] as const,
  // A child's cosmetics catalog (per-child owned/equipped/affordable/unlocked state). `"self"` = the
  // signed-in child; a guardian viewing a child keys by the child's id.
  cosmeticsCatalog: (childId?: string | null) =>
    ['cosmetics', 'catalog', childId ?? 'self'] as const,
  // A child's currently-equipped cosmetics (one per slot). Same self/child keying as the catalog.
  cosmeticsEquipped: (childId?: string | null) =>
    ['cosmetics', 'equipped', childId ?? 'self'] as const,
  // The household's active cooperative family quest(s) (ADR-0029). Everyone reads the SAME shared card,
  // so this is a single household-scoped key (no childId) — the family quest card invalidates it as
  // kids complete chores/habits so the shared bar moves. A chore/habit complete invalidates this key.
  questsActive: () => ['quests', 'active'] as const,
  // The household's quest history (active + completed + archived), cursor-paginated. Drives the
  // guardian management list; under the same `["quests"]` prefix so a create/archive invalidation
  // reaches both the card and the history.
  questsHistory: () => ['quests', 'history'] as const,
};

// --- task reads ---

export function useDayTasks(
  assigneeId: string,
  date: string,
  options?: QueryOpts<DayTasksResponse>,
): UseQueryResult<DayTasksResponse, Error> {
  return useQuery({
    queryKey: familyKeys.day(assigneeId, date),
    queryFn: ({ signal }) => tasksControllerDayV1({ assigneeId, date }, undefined, signal),
    ...options,
  });
}

/**
 * An assignee's open anytime "missions" (ADR-0141): open-ended one-time chores still in play, regardless
 * of date. Backs the kid Home Missions lane (rendered on today only).
 */
export function useMissions(
  assigneeId: string,
  options?: QueryOpts<MissionsResponse>,
): UseQueryResult<MissionsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.missions(assigneeId),
    queryFn: ({ signal }) => tasksControllerMissionsV1({ assigneeId }, undefined, signal),
    ...options,
  });
}

/**
 * An assignee's FINISHED anytime "missions" (ADR-0141): open-ended one-time chores that are fully done
 * (`approved`), newest-finished first. Backs the read-only kid Missions history shelf (`/missions/finished`).
 */
export function useFinishedMissions(
  assigneeId: string,
  options?: QueryOpts<MissionsResponse>,
): UseQueryResult<MissionsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.missionsFinished(assigneeId),
    queryFn: ({ signal }) => tasksControllerFinishedMissionsV1({ assigneeId }, undefined, signal),
    ...options,
  });
}

export function useWeekAgenda(
  assigneeId: string,
  weekStart: string,
  options?: QueryOpts<WeekAgendaResponse>,
): UseQueryResult<WeekAgendaResponse, Error> {
  return useQuery({
    queryKey: familyKeys.week(assigneeId, weekStart),
    queryFn: ({ signal }) => tasksControllerWeekV1({ assigneeId, weekStart }, undefined, signal),
    ...options,
  });
}

export function useApprovalsQueue(
  options?: QueryOpts<ApprovalItemResponse[]>,
): UseQueryResult<ApprovalItemResponse[], Error> {
  return useQuery({
    queryKey: familyKeys.approvals(),
    queryFn: ({ signal }) => tasksControllerApprovalsV1(undefined, signal),
    ...options,
  });
}

/**
 * An assignee's daily chore-completion trend over `[from, to]` (`GET /v1/tasks/completion-trend`) —
 * per-day scheduled vs approved/missed/skipped counts, gap-filled. The client derives the completion
 * ratio for a calm "showing up" trend (never a red miss alarm — ADR-0029). `from`/`to` are
 * household-local `YYYY-MM-DD`; `assigneeId` defaults to the caller server-side (a child is self-scoped;
 * a guardian may pass any member id for oversight).
 */
export function useCompletionTrend(
  assigneeId: string,
  from: string,
  to: string,
  options?: QueryOpts<CompletionTrendResponse>,
): UseQueryResult<CompletionTrendResponse, Error> {
  return useQuery({
    queryKey: familyKeys.completionTrend(assigneeId, from, to),
    queryFn: ({ signal }) =>
      tasksControllerCompletionTrendV1({ assigneeId, from, to }, undefined, signal),
    enabled: !!assigneeId && !!from && !!to,
    ...options,
  });
}

export function useOccurrence(
  occurrenceId: string,
  options?: QueryOpts<OccurrenceResponse>,
): UseQueryResult<OccurrenceResponse, Error> {
  return useQuery({
    queryKey: familyKeys.occurrence(occurrenceId),
    queryFn: ({ signal }) => occurrencesControllerGetV1(occurrenceId, undefined, signal),
    enabled: !!occurrenceId,
    ...options,
  });
}

// --- task writes (idempotent where the backend marks @Idempotent) ---

/**
 * A chore day addressed by its natural key `(templateId, localDate)` (ADR-0142). A guardian may pass the
 * card's `assigneeId`; a child's is forced to themselves server-side, so it is optional here. There is no
 * `planned` occurrence to address by id anymore — the handler lazily logs the acted row.
 */
export interface ChoreActionAddress {
  readonly templateId: string;
  readonly localDate: string;
  readonly assigneeId?: string;
}

/**
 * A chore action's client-side identity: the card's `occ.id` (the deterministic `virtual:…` id for an
 * unlogged day). Carried through the mutation so the consuming feature's optimistic cache surgery +
 * per-row pending state can key on it; it is NEVER sent on the wire (the generated request functions
 * ignore it — a chore is addressed by `templateId` + `localDate`).
 */
export interface OptimisticCardId {
  readonly optimisticId: string;
}

export interface CompleteTaskVars extends ChoreActionAddress, OptimisticCardId {
  readonly photoProofKey?: string | null;
  readonly idempotencyKey: string;
}

export function useCompleteTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, CompleteTaskVars, TContext>,
): UseMutationResult<void, Error, CompleteTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId, localDate, assigneeId, photoProofKey, idempotencyKey }) =>
      choreActionsControllerCompleteV1(
        // Optional fields are dropped by JSON serialization when undefined; the backend coalesces a
        // missing `photoProofKey` to `null` (`dto.photoProofKey ?? null`).
        { templateId, localDate, assigneeId, photoProofKey: photoProofKey ?? undefined },
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface UncompleteTaskVars extends ChoreActionAddress, OptimisticCardId {
  /** Stable per-intent key so an offline undo queues in the PWA outbox and replays exactly once. */
  readonly idempotencyKey: string;
}

/**
 * UNDO a completed chore (ADR-0141): deletes the action-log row so the day returns to its computed
 * "available" state (an auto-credited same-day undo also claws the coins back server-side). The
 * `POST /v1/tasks/uncomplete` route is `@Idempotent`, so we carry an `Idempotency-Key` — the offline
 * outbox only queues writes that carry one, making an undo attempted offline durable instead of lost.
 */
export function useUncompleteTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UncompleteTaskVars, TContext>,
): UseMutationResult<void, Error, UncompleteTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId, localDate, assigneeId, idempotencyKey }) =>
      choreActionsControllerUncompleteV1(
        { templateId, localDate, assigneeId },
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface UploadPhotoProofVars {
  readonly occurrenceId: string;
  /** The captured/selected image file (validated by the caller for type + size before upload). */
  readonly file: File;
}

/**
 * Upload a chore photo proof (`POST /v1/tasks/occurrences/:occurrenceId/photo-proof`, multipart field
 * `file`) and return the opaque `{ photoProofKey }` the caller then passes to `useCompleteTask` so the
 * occurrence records it. The upload itself is NOT idempotent (no key): a child only ever uploads after
 * picking a photo, and a re-pick simply mints a fresh key — there's no money/ledger side-effect to
 * double-apply, and a stranded upload (never completed) is harmless. Errors propagate so the capture
 * UI can show a kind retry message.
 */
export function useUploadPhotoProof<TContext = unknown>(
  options?: UseMutationOptions<UploadPhotoProofResponse, Error, UploadPhotoProofVars, TContext>,
): UseMutationResult<UploadPhotoProofResponse, Error, UploadPhotoProofVars, TContext> {
  return useMutation({
    mutationFn: ({ occurrenceId, file }) => photoProofControllerUploadV1(occurrenceId, { file }),
    ...options,
  });
}

export interface RedoTaskVars {
  readonly occurrenceId: string;
  /** Fresh photo proof for a photo-required chore (the rejection purged the previous photo). */
  readonly photoProofKey?: string | null;
  readonly idempotencyKey: string;
}

/**
 * Re-submit a rejected chore for approval (`rejected → pending_approval`). Mirrors `useCompleteTask`:
 * the `POST .../redo` route is `@Idempotent`, so we carry an `Idempotency-Key` and an offline replay
 * never double-applies. A photo-proof chore re-submits WITH a fresh `photoProofKey` (rejection purged
 * the old photo — the backend requires a new one when the template demands it); a plain chore sends
 * an empty body.
 */
export function useRedoTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, RedoTaskVars, TContext>,
): UseMutationResult<void, Error, RedoTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ occurrenceId, photoProofKey, idempotencyKey }) =>
      occurrencesControllerRedoV1(
        occurrenceId,
        photoProofKey ? { photoProofKey } : {},
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface BackfillTaskVars {
  readonly occurrenceId: string;
  /** Fresh photo proof for a photo-required chore (same anti-gaming rule as complete/redo). */
  readonly photoProofKey?: string | null;
  readonly idempotencyKey: string;
}

/**
 * Log a missed/past chore for its real scheduled day (`POST .../backfill`, ADR-0040). Mirrors
 * `useRedoTask`: the route is `@Idempotent`, so we MUST carry an `Idempotency-Key` (the raw generated
 * hook sends none → 400 "Idempotency-Key header is required"). A photo-proof chore back-fills WITH a
 * fresh `photoProofKey`; a plain chore sends an empty body.
 */
export function useBackfillTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, BackfillTaskVars, TContext>,
): UseMutationResult<void, Error, BackfillTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ occurrenceId, photoProofKey, idempotencyKey }) =>
      occurrencesControllerBackfillV1(
        occurrenceId,
        photoProofKey ? { photoProofKey } : {},
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface SkipTaskVars extends ChoreActionAddress, OptimisticCardId {
  /** Stable per-intent key so an offline skip queues in the PWA outbox and replays exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Skip a chore for its day (kind, never punitive). The `POST /v1/tasks/skip` route is `@Idempotent`,
 * so we carry an `Idempotency-Key` — the offline outbox only queues writes that carry one, which
 * makes a skip attempted offline durable instead of silently lost.
 */
export function useSkipTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SkipTaskVars, TContext>,
): UseMutationResult<void, Error, SkipTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId, localDate, assigneeId, idempotencyKey }) =>
      choreActionsControllerSkipV1(
        { templateId, localDate, assigneeId },
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface PhotoCompleteTaskVars extends ChoreActionAddress, OptimisticCardId {
  /** The captured/selected image file (validated by the caller for type + size before upload). */
  readonly file: File;
  /** Stable per-intent key so an offline photo completion queues in the outbox and replays once. */
  readonly idempotencyKey: string;
}

/**
 * Complete a PHOTO-PROOF chore addressed by `(templateId, localDate)` in one multipart step (ADR-0142
 * F8): a virtual card has no occurrence id, so the image and the completion travel together
 * (`POST /v1/tasks/photo-proof-complete`, multipart field `file`). The route is `@Idempotent`, so we
 * carry an `Idempotency-Key`. Used for the FIRST completion of a virtual photo-proof card; a
 * redo/backfill of an already-real occurrence still uses {@link useUploadPhotoProof} + the id route.
 */
export function usePhotoCompleteTask<TContext = unknown>(
  options?: UseMutationOptions<void, Error, PhotoCompleteTaskVars, TContext>,
): UseMutationResult<void, Error, PhotoCompleteTaskVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId, localDate, assigneeId, file, idempotencyKey }) =>
      photoProofControllerPhotoCompleteV1(
        { file, templateId, localDate, assigneeId },
        idempotent(idempotencyKey),
      ),
    ...options,
  });
}

export interface ApproveOccurrenceVars {
  readonly occurrenceId: string;
  readonly note?: string;
  readonly idempotencyKey: string;
}

export function useApproveOccurrence<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ApproveOccurrenceVars, TContext>,
): UseMutationResult<void, Error, ApproveOccurrenceVars, TContext> {
  return useMutation({
    mutationFn: ({ occurrenceId, note, idempotencyKey }) => {
      const body: ApproveOccurrenceDto = note ? { note } : {};
      return occurrencesControllerApproveV1(occurrenceId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface RejectOccurrenceVars {
  readonly occurrenceId: string;
  readonly note: string;
  readonly idempotencyKey: string;
}

/**
 * Return a chore for changes ("Ask to redo"). The `POST .../reject` route is `@Idempotent` on the
 * backend (same as `useApproveOccurrence`), so we carry an `Idempotency-Key` — without one an offline
 * replay of the same review tap could re-run the reject transition twice.
 */
export function useRejectOccurrence<TContext = unknown>(
  options?: UseMutationOptions<void, Error, RejectOccurrenceVars, TContext>,
): UseMutationResult<void, Error, RejectOccurrenceVars, TContext> {
  return useMutation({
    mutationFn: ({ occurrenceId, note, idempotencyKey }) => {
      const body: RejectOccurrenceDto = { note };
      return occurrencesControllerRejectV1(occurrenceId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

// --- identity reads/writes ---

export function useMembers(
  options?: QueryOpts<MembersControllerListV1200>,
): UseQueryResult<MembersControllerListV1200, Error> {
  // `GET /v1/members` is cursor-paginated (`{ data, nextCursor, hasMore }`), like the ledger — the
  // household's members come back under `.data`. Consumers read `result.data.data`. We request the
  // API's MAX page (100, vs the default 20): rosters grow past 20 in practice (every invited
  // relative adds a row and there is no member delete), and ordering is `created_at DESC`, so a
  // default-size page would silently drop the OLDEST members (typically the kids and the owner)
  // from the admin roster and the role member-counts. Surfaces that must be complete should still
  // check `hasMore` and say so when a 100+ roster is truncated.
  return useQuery({
    queryKey: familyKeys.members(),
    queryFn: ({ signal }) => membersControllerListV1({ limit: 100 }, undefined, signal),
    ...options,
  });
}

export function useMember(
  memberId: string,
  options?: QueryOpts<MemberResponse>,
): UseQueryResult<MemberResponse, Error> {
  return useQuery({
    queryKey: familyKeys.member(memberId),
    queryFn: ({ signal }) => membersControllerGetV1(memberId, undefined, signal),
    enabled: !!memberId,
    ...options,
  });
}

export interface UpdateMemberVars {
  readonly memberId: string;
  readonly data: UpdateMemberProfileDto;
}

export function useUpdateMember<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateMemberVars, TContext>,
): UseMutationResult<void, Error, UpdateMemberVars, TContext> {
  return useMutation({
    mutationFn: ({ memberId, data }) => membersControllerUpdateProfileV1(memberId, data),
    ...options,
  });
}

// --- economy reads ---

export function useChildBalance(
  childId: string,
  options?: QueryOpts<ChildBalanceResponse>,
): UseQueryResult<ChildBalanceResponse, Error> {
  return useQuery({
    queryKey: familyKeys.balance(childId),
    queryFn: ({ signal }) => balancesControllerBalanceV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * A child's daily coin-balance trend over `[from, to]` (`GET /v1/economy/children/:childId/balance-trend`)
 * — a gap-filled running-balance series reconstructed server-side from the append-only ledger. Powers the
 * Wallet sparkline (the kid's own; a guardian viewing a child sees the same). `from`/`to` are
 * household-local `YYYY-MM-DD`. The API self-scopes a child to their own; a guardian may pass any child id.
 */
export function useBalanceTrend(
  childId: string,
  from: string,
  to: string,
  options?: QueryOpts<ChildBalanceTrendResponse>,
): UseQueryResult<ChildBalanceTrendResponse, Error> {
  return useQuery({
    queryKey: familyKeys.balanceTrend(childId, from, to),
    queryFn: ({ signal }) =>
      balancesControllerBalanceTrendV1(childId, { from, to }, undefined, signal),
    enabled: !!childId && !!from && !!to,
    ...options,
  });
}

/**
 * A child's chore + learning daily streaks (`GET /v1/economy/children/:childId/streaks`, ADR-0080) —
 * current run + personal best, grace-aware. Feeds the kid "your streaks" strip, the parent per-child view,
 * and un-blind bonus authoring. A child self-reads their own; a guardian reads any child's.
 */
export function useChildStreaks(
  childId: string,
  options?: QueryOpts<ChildStreaksResponse>,
): UseQueryResult<ChildStreaksResponse, Error> {
  return useQuery({
    queryKey: familyKeys.streaks(childId),
    queryFn: ({ signal }) => balancesControllerStreaksV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * A child's projected coin income (`GET /v1/economy/children/:childId/income-forecast`, ADR-0090) — a
 * `realistic` earning rate (recent actual pace) with a rules-based `potential` ceiling (assigned chores +
 * the active learning goal), each as per-day/week/month, plus a `basis` flag. The guardian's pricing ruler:
 * "this reward is ~N days of earning". A child self-reads their own; a guardian reads any child's.
 */
export function useIncomeForecast(
  childId: string,
  options?: QueryOpts<IncomeForecastResponse>,
): UseQueryResult<IncomeForecastResponse, Error> {
  return useQuery({
    queryKey: familyKeys.incomeForecast(childId),
    queryFn: ({ signal }) => balancesControllerIncomeForecastV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * The reward a child is saving toward (`GET /v1/economy/children/:childId/reward-goal`, ADR-0094) — just
 * the pinned `rewardId` (or null). The client resolves the reward from the shop list it already has and
 * derives the progress (balance vs cost) + the days-to-earn hint. A child self-reads; a guardian reads any.
 */
export function useRewardGoal(
  childId: string,
  options?: QueryOpts<RewardGoalResponse>,
): UseQueryResult<RewardGoalResponse, Error> {
  return useQuery({
    queryKey: familyKeys.rewardGoal(childId),
    queryFn: ({ signal }) => rewardGoalControllerGetV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

export interface SetRewardGoalVars {
  readonly childId: string;
  readonly rewardId: string;
}

/**
 * Pin a reward as the child's "saving toward" goal (`PUT /v1/economy/children/:childId/reward-goal`, 204,
 * ADR-0094). Kid-only (`reward:favorite`); upserts to one pin per child and auto-favorites it. Invalidates
 * the child's reward-goal + rewards caches so the hero card + wishlist heart refresh.
 */
export function useSetRewardGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, SetRewardGoalVars, TContext>,
): UseMutationResult<void, Error, SetRewardGoalVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, rewardId }) => rewardGoalControllerSetV1(childId, { rewardId }),
    ...options,
    onSuccess: (data, vars, ...rest) => {
      void qc.invalidateQueries({ queryKey: familyKeys.rewardGoal(vars.childId) });
      void qc.invalidateQueries({ queryKey: familyKeys.rewards(vars.childId) });
      options?.onSuccess?.(data, vars, ...rest);
    },
  });
}

export interface ClearRewardGoalVars {
  readonly childId: string;
}

/**
 * Clear a child's "saving toward" pin (`DELETE /v1/economy/children/:childId/reward-goal`, 204, ADR-0094).
 * Kid-only; idempotent; leaves the wishlist favorite intact. Invalidates the reward-goal cache.
 */
export function useClearRewardGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ClearRewardGoalVars, TContext>,
): UseMutationResult<void, Error, ClearRewardGoalVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId }) => rewardGoalControllerClearV1(childId),
    ...options,
    onSuccess: (data, vars, ...rest) => {
      void qc.invalidateQueries({ queryKey: familyKeys.rewardGoal(vars.childId) });
      options?.onSuccess?.(data, vars, ...rest);
    },
  });
}

/**
 * A child's transparent "Ways to earn coins" (`GET /v1/economy/children/:childId/ways-to-earn`,
 * ADR-0080) — the read-only mirror of the parent Ways-to-earn surface: the mastery + default-chore
 * reward amounts plus the household's ACTIVE streak / topic-mastery bonuses, each enriched with this
 * child's live progress (`current` + `nextRung`) for the ADR-0061 progress meter. The API self-scopes a
 * child to their own; a guardian may pass another child's id. Composed alongside {@link useGoalStatus}.
 */
export function useWaysToEarn(
  childId: string,
  options?: QueryOpts<WaysToEarnResponse>,
): UseQueryResult<WaysToEarnResponse, Error> {
  return useQuery({
    queryKey: familyKeys.waysToEarn(childId),
    queryFn: ({ signal }) => balancesControllerWaysToEarnV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * A child's active learning-goal status (`GET /v1/learning/goal-status`, ADR-0080) — the daily session
 * target + its coin reward + today's session count + whether it's met. `childId` is passed through as the
 * optional query param (a guardian previewing a specific kid); a child caller is self-scoped server-side.
 * Drives the "learning goal" row on the kid Ways-to-earn screen.
 */
export function useGoalStatus(
  childId: string,
  options?: QueryOpts<GoalStatusResponse>,
): UseQueryResult<GoalStatusResponse, Error> {
  return useQuery({
    queryKey: familyKeys.goalStatus(childId),
    queryFn: ({ signal }) =>
      learningControllerGoalStatusV1(childId ? { childId } : undefined, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * A child's daily learning-goal adherence trend (`GET /v1/learning/goal-trend?childId&from&to`) — per
 * local day { date, sessions, goalMet } plus the current `dailyGoal`, gap-filled. `childId` defaults to
 * the caller server-side (a child self-scoped; a guardian may pass any child id). Drives the "days you hit
 * your goal" strip on the learning progress page.
 */
export function useGoalTrend(
  childId: string,
  from: string,
  to: string,
  options?: QueryOpts<GoalTrendResponse>,
): UseQueryResult<GoalTrendResponse, Error> {
  return useQuery({
    queryKey: familyKeys.goalTrend(childId, from, to),
    queryFn: ({ signal }) =>
      learningControllerGoalTrendV1({ childId, from, to }, undefined, signal),
    enabled: !!childId && !!from && !!to,
    ...options,
  });
}

export function useLedger(
  childId: string,
  currency: string | null,
  cursor: string | null,
  options?: QueryOpts<BalancesControllerLedgerV1200>,
): UseQueryResult<BalancesControllerLedgerV1200, Error> {
  return useQuery({
    queryKey: [...familyKeys.ledger(childId, currency), cursor ?? 'first'],
    queryFn: ({ signal }) =>
      balancesControllerLedgerV1(
        childId,
        {
          // `currency` is a loose `string | null` on this hook's public surface; the generated param
          // type narrows it to the wire enum. Same value still reaches the server (server re-validates).
          ...(currency
            ? {
                currency: currency as BalancesControllerLedgerV1Params['currency'],
              }
            : {}),
          ...(cursor ? { cursor } : {}),
        },
        undefined,
        signal,
      ),
    enabled: !!childId,
    ...options,
  });
}

export function useRewards(
  forChildId: string | null,
  options?: QueryOpts<RewardResponse[]>,
): UseQueryResult<RewardResponse[], Error> {
  return useQuery({
    queryKey: familyKeys.rewards(forChildId),
    queryFn: ({ signal }) =>
      rewardsControllerListV1(forChildId ? { forChildId } : undefined, undefined, signal),
    ...options,
  });
}

export function useReward(
  rewardId: string,
  forChildId: string | null,
  options?: QueryOpts<RewardResponse>,
): UseQueryResult<RewardResponse, Error> {
  return useQuery({
    queryKey: familyKeys.reward(rewardId, forChildId),
    queryFn: ({ signal }) =>
      rewardsControllerGetV1(rewardId, forChildId ? { forChildId } : undefined, undefined, signal),
    enabled: !!rewardId,
    ...options,
  });
}

/**
 * A child's savings GOALS board (`GET /v1/economy/savings/children/:childId` → `SavingsGoalResponse[]`,
 * ADR-0038). A child may run several goals at once; the list is ordered by `position` ascending and an
 * empty board is a normal `[]` (no goals yet). Pass `includeArchived` to also surface archived cards.
 * Each card carries `emoji` + `colorToken` (resolve the token to a swatch) + `savedAmount`/`targetAmount`.
 */
export function useSavingsGoals(
  childId: string,
  options?: QueryOpts<SavingsGoalResponse[]> & { includeArchived?: boolean },
): UseQueryResult<SavingsGoalResponse[], Error> {
  const { includeArchived, ...queryOptions } = options ?? {};
  const params = includeArchived ? { includeArchived: true } : undefined;
  return useQuery({
    queryKey: familyKeys.savings(childId, includeArchived),
    queryFn: ({ signal }) => savingsControllerListForChildV1(childId, params, undefined, signal),
    enabled: !!childId,
    ...queryOptions,
  });
}

export function useActiveHolds(
  childId: string,
  options?: QueryOpts<HoldResponse[]>,
): UseQueryResult<HoldResponse[], Error> {
  return useQuery({
    queryKey: familyKeys.holds(childId),
    queryFn: ({ signal }) => holdsControllerListForChildV1(childId, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * Every active hold across the whole household, enriched with display labels — backs the global
 * "what's paused" banner that every guardian + relative sees (so e.g. grandma doesn't grant a held
 * treat). `GET /v1/economy/holds/active` is `household:read` (every member holds it).
 */
export function useHouseholdActiveHolds(
  options?: QueryOpts<HouseholdHoldResponse[]>,
): UseQueryResult<HouseholdHoldResponse[], Error> {
  return useQuery({
    queryKey: familyKeys.householdHolds(),
    queryFn: ({ signal }) => holdsControllerListActiveV1(undefined, signal),
    ...options,
  });
}

/**
 * The signed-in kid's own redemption requests ("My requests"). `GET /v1/economy/redemptions` is
 * `household:read` (the Child preset holds it) and the controller forces a child caller to their own
 * redemptions, so a kid never sees a sibling's. The page is `{ data, nextCursor, hasMore }`; a kid's
 * recent requests fit the first page (no further paging on this surface).
 */
export function useMyRedemptions(
  options?: QueryOpts<RedemptionsControllerListV1200>,
): UseQueryResult<RedemptionsControllerListV1200, Error> {
  return useQuery({
    queryKey: familyKeys.redemptions(),
    queryFn: ({ signal }) => redemptionsControllerListV1(undefined, undefined, signal),
    ...options,
  });
}

// --- reward photo (creator/relative-attached; mirrors the member-avatar photo, ADR-0037) ---

export interface UploadRewardPhotoVars {
  readonly rewardId: string;
  /** The processed WebP blob, wrapped as a File so the multipart `file` part is well-formed. */
  readonly file: File;
}

/**
 * Attach an uploaded photo to a reward (`POST /v1/economy/rewards/:rewardId/photo`, multipart field
 * `file` → the updated `RewardResponse`). Hand-written (the endpoint is not yet in the generated client)
 * — mirrors how `useUpdateQuest` calls `apiFetch` directly + how `useUploadPhotoProof` sends a multipart
 * `file`. NOT idempotent (no key): a re-pick simply mints a fresh stored object key and the prior one is
 * deleted server-side; there is no money/ledger side-effect to double-apply. `reward:manage:own` gated
 * (the server re-checks BOLA + the `:own`/`:any` ownership split). Callers invalidate the reward + list.
 */
export function useUploadRewardPhoto<TContext = unknown>(
  options?: UseMutationOptions<RewardResponse, Error, UploadRewardPhotoVars, TContext>,
): UseMutationResult<RewardResponse, Error, UploadRewardPhotoVars, TContext> {
  return useMutation({
    mutationFn: ({ rewardId, file }) => {
      const form = new FormData();
      form.append('file', file);
      return apiFetch<RewardResponse>({
        url: `/v1/economy/rewards/${rewardId}/photo`,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data: form,
      });
    },
    ...options,
  });
}

export interface DeleteRewardPhotoVars {
  readonly rewardId: string;
}

/**
 * Remove a reward's uploaded photo (`DELETE /v1/economy/rewards/:rewardId/photo`, 204) — reverts the
 * reward to its icon glyph. Hand-written (not yet generated). `reward:manage:own` gated. Idempotent
 * server-side (removing an icon-default reward is a no-op success). Callers invalidate the reward + list.
 */
export function useDeleteRewardPhoto<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DeleteRewardPhotoVars, TContext>,
): UseMutationResult<void, Error, DeleteRewardPhotoVars, TContext> {
  return useMutation({
    mutationFn: ({ rewardId }) =>
      apiFetch<void>({
        url: `/v1/economy/rewards/${rewardId}/photo`,
        method: 'DELETE',
      }),
    ...options,
  });
}

/**
 * Auth-gated fetcher for an uploaded reward photo (`GET /v1/economy/rewards/photo/:key` → image bytes
 * as a `Blob`). Hand-written (not yet generated) — flows through the shared axios instance so the
 * `Authorization` Bearer header + 401-refresh are applied, exactly like the generated serve functions.
 * Consumed by the `AuthImage` component. Returns a `Blob` the component turns into an object URL.
 */
export async function fetchRewardPhotoBlob(key: string, signal?: AbortSignal): Promise<Blob> {
  const response = await axiosInstance.get<Blob>(`/v1/economy/rewards/photo/${key}`, {
    responseType: 'blob',
    signal,
  });
  return response.data;
}

// --- mission cover photo (guardian-attached; mirrors the reward photo, ADR-0141) ---

export interface UploadTemplateCoverVars {
  readonly templateId: string;
  /** The processed WebP blob, wrapped as a File so the multipart `file` part is well-formed. */
  readonly file: File;
}

/**
 * Attach an uploaded cover photo to a chore/mission template (`POST /v1/tasks/templates/:templateId/photo`,
 * multipart field `file` → the updated `TemplateResponse`). Hand-written, mirrors `useUploadRewardPhoto`.
 * NOT idempotent (a re-pick mints a fresh key; the prior object is deleted server-side). `chore:update`
 * gated (the server re-checks BOLA). Callers invalidate the template list.
 */
export function useUploadTemplateCover<TContext = unknown>(
  options?: UseMutationOptions<TemplateResponse, Error, UploadTemplateCoverVars, TContext>,
): UseMutationResult<TemplateResponse, Error, UploadTemplateCoverVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId, file }) => {
      const form = new FormData();
      form.append('file', file);
      return apiFetch<TemplateResponse>({
        url: `/v1/tasks/templates/${templateId}/photo`,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data: form,
      });
    },
    ...options,
  });
}

export interface DeleteTemplateCoverVars {
  readonly templateId: string;
}

/**
 * Remove a chore/mission template's cover photo (`DELETE /v1/tasks/templates/:templateId/photo`, 204) —
 * reverts to the icon glyph. Hand-written, mirrors `useDeleteRewardPhoto`. `chore:update` gated;
 * idempotent server-side. Callers invalidate the template list.
 */
export function useDeleteTemplateCover<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DeleteTemplateCoverVars, TContext>,
): UseMutationResult<void, Error, DeleteTemplateCoverVars, TContext> {
  return useMutation({
    mutationFn: ({ templateId }) =>
      apiFetch<void>({
        url: `/v1/tasks/templates/${templateId}/photo`,
        method: 'DELETE',
      }),
    ...options,
  });
}

/**
 * Auth-gated fetcher for an uploaded mission cover (`GET /v1/tasks/templates/photo/:key` → image bytes
 * as a `Blob`). Hand-written, mirrors `fetchRewardPhotoBlob` — flows through the shared axios instance
 * so the Bearer header + 401-refresh are applied. Consumed by the `AuthImage` component.
 */
export async function fetchTemplateCoverBlob(key: string, signal?: AbortSignal): Promise<Blob> {
  const response = await axiosInstance.get<Blob>(`/v1/tasks/templates/photo/${key}`, {
    responseType: 'blob',
    signal,
  });
  return response.data;
}

// --- economy writes ---

export interface RedeemRewardVars {
  readonly rewardId: string;
  /** Stable per-intent key so an offline replay never creates a duplicate redemption request. */
  readonly idempotencyKey: string;
}

export function useRedeemReward<TContext = unknown>(
  options?: UseMutationOptions<RequestRedemptionResponse, Error, RedeemRewardVars, TContext>,
): UseMutationResult<RequestRedemptionResponse, Error, RedeemRewardVars, TContext> {
  return useMutation({
    mutationFn: ({ rewardId, idempotencyKey }) => {
      const body: CreateRedemptionDto = { rewardId };
      return redemptionsControllerRequestV1(body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface DecideOwnRedemptionVars {
  readonly redemptionId: string;
  /** Stable per-intent key so an offline replay of the same tap never re-runs the FSM transition. */
  readonly idempotencyKey: string;
}

/**
 * Cancel one's OWN pending redemption (`POST /v1/economy/redemptions/:id/cancel`, 204, ADR-0065). A kid
 * backs out of a request before a guardian decides — no ledger effect (coins are debited only at grant).
 * `@Idempotent` on the backend, so the `Idempotency-Key` makes an offline replay a no-op. The consuming
 * feature invalidates `familyKeys.redemptions()` so "My requests" reflects the cancellation.
 */
export function useCancelRedemption<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DecideOwnRedemptionVars, TContext>,
): UseMutationResult<void, Error, DecideOwnRedemptionVars, TContext> {
  return useMutation({
    mutationFn: ({ redemptionId, idempotencyKey }) =>
      redemptionsControllerCancelV1(redemptionId, idempotent(idempotencyKey)),
    ...options,
  });
}

/**
 * Mark a granted reward as actually received (`POST /v1/economy/redemptions/:id/collect`, 204, ADR-0065)
 * — the payoff beat that closes the loop. Either the owning child ("Got it!") or a guardian may collect;
 * no ledger effect (the coins were debited at grant). `@Idempotent`, so the `Idempotency-Key` makes an
 * offline replay a no-op. The consuming feature invalidates `familyKeys.redemptions()` so the treats tray
 * clears the collected row.
 */
export function useCollectRedemption<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DecideOwnRedemptionVars, TContext>,
): UseMutationResult<void, Error, DecideOwnRedemptionVars, TContext> {
  return useMutation({
    mutationFn: ({ redemptionId, idempotencyKey }) =>
      redemptionsControllerCollectV1(redemptionId, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface FavoriteRewardVars {
  readonly rewardId: string;
}

/**
 * Add a reward to the signed-in child's wishlist (`POST /v1/economy/rewards/:rewardId/favorite`, 204,
 * ADR-0066). Kid-only (`reward:favorite`); idempotent server-side (a re-favorite is a no-op). No ledger
 * effect. The consuming feature invalidates `familyKeys.rewards(childId)` so the heart + wishlist refresh.
 */
export function useFavoriteReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, FavoriteRewardVars, TContext>,
): UseMutationResult<void, Error, FavoriteRewardVars, TContext> {
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerFavoriteV1(rewardId),
    ...options,
  });
}

/**
 * Remove a reward from the signed-in child's wishlist (`DELETE /v1/economy/rewards/:rewardId/favorite`,
 * 204, ADR-0066). Kid-only; idempotent. Mirrors {@link useFavoriteReward}; callers invalidate the
 * rewards cache so the heart un-fills and the wishlist drops the card.
 */
export function useUnfavoriteReward<TContext = unknown>(
  options?: UseMutationOptions<void, Error, FavoriteRewardVars, TContext>,
): UseMutationResult<void, Error, FavoriteRewardVars, TContext> {
  return useMutation({
    mutationFn: ({ rewardId }) => rewardsControllerUnfavoriteV1(rewardId),
    ...options,
  });
}

export interface CreateSavingsGoalVars {
  /**
   * The target child. A guardian names the child; a CHILD caller omits it (the controller forces a
   * child to their own scope regardless), so this is optional for the self path.
   */
  readonly childId?: string;
  readonly name: string;
  /** Target amount in minor units (cents). */
  readonly targetAmount: number;
  /** Optional personalization (a curated emoji glyph + a curated palette token); defaults applied. */
  readonly emoji?: string;
  readonly colorToken?: CreateSavingsGoalDto['colorToken'];
  /**
   * Stable per-intent key so an offline replay never opens a DUPLICATE goal. The route is
   * `@Idempotent()` on the backend; a child may run several goals up to a soft cap (ADR-0038).
   */
  readonly idempotencyKey: string;
}

/**
 * Create a savings goal (`POST /v1/economy/savings` → `{ savingsGoalId }`, ADR-0038). The base
 * capability is `household:read` (a CHILD holds it), and the backend self-scopes a child caller to
 * their own goal (a guardian supplies `childId`). A child may run SEVERAL goals at once (bounded by a
 * soft cap — `SAVINGS_GOAL_LIMIT_REACHED` once exceeded). The `Idempotency-Key` makes an offline replay
 * a no-op. Callers invalidate `familyKeys.savings(childId)` on success so the board appends the new card.
 */
export function useCreateSavingsGoal<TContext = unknown>(
  options?: UseMutationOptions<CreateSavingsGoalResponse, Error, CreateSavingsGoalVars, TContext>,
): UseMutationResult<CreateSavingsGoalResponse, Error, CreateSavingsGoalVars, TContext> {
  return useMutation({
    mutationFn: ({ childId, name, targetAmount, emoji, colorToken, idempotencyKey }) => {
      const body: CreateSavingsGoalDto = {
        name,
        targetAmount,
        ...(childId ? { childId } : {}),
        ...(emoji ? { emoji } : {}),
        ...(colorToken ? { colorToken } : {}),
      };
      return savingsControllerCreateV1(body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface UpdateSavingsGoalVars {
  readonly savingsGoalId: string;
  /** Only provided fields change (name / target / emoji / colorToken / board position). */
  readonly name?: string;
  readonly targetAmount?: number;
  readonly emoji?: string;
  readonly colorToken?: UpdateSavingsGoalDto['colorToken'];
  readonly position?: number;
}

/**
 * Edit a savings goal (`PATCH /v1/economy/savings/:id`, ADR-0038) — rename / re-target / re-personalize
 * / reorder its card on the board. Self-scoped for a child; a guardian may edit any in the household.
 */
export function useUpdateSavingsGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateSavingsGoalVars, TContext>,
): UseMutationResult<void, Error, UpdateSavingsGoalVars, TContext> {
  return useMutation({
    mutationFn: ({ savingsGoalId, name, targetAmount, emoji, colorToken, position }) => {
      const body: UpdateSavingsGoalDto = {
        ...(name !== undefined ? { name } : {}),
        ...(targetAmount !== undefined ? { targetAmount } : {}),
        ...(emoji !== undefined ? { emoji } : {}),
        ...(colorToken !== undefined ? { colorToken } : {}),
        ...(position !== undefined ? { position } : {}),
      };
      return savingsControllerUpdateV1(savingsGoalId, body);
    },
    ...options,
  });
}

export interface ArchiveSavingsGoalVars {
  readonly savingsGoalId: string;
  /** Stable per-intent key so an offline replay archives exactly once (the route is `@Idempotent`). */
  readonly idempotencyKey: string;
}

/**
 * Archive a savings goal (`POST /v1/economy/savings/:id/archive`, ADR-0038) — removes the card from the
 * live board (and frees a slot toward the soft cap). Idempotent end to end.
 */
export function useArchiveSavingsGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveSavingsGoalVars, TContext>,
): UseMutationResult<void, Error, ArchiveSavingsGoalVars, TContext> {
  return useMutation({
    mutationFn: ({ savingsGoalId, idempotencyKey }) =>
      savingsControllerArchiveV1(savingsGoalId, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface CompleteSavingsGoalVars {
  readonly savingsGoalId: string;
  /** Stable per-intent key so an offline replay completes exactly once (the route is `@Idempotent`). */
  readonly idempotencyKey: string;
}

/**
 * Complete (claim) a reached savings goal (`POST /v1/economy/savings/:id/complete`, ADR-0038) — a
 * celebrated achievement that moves the card off the live board into the Achieved history (distinct
 * from archiving). Only a `reached` goal may be completed; idempotent end to end. Callers invalidate
 * `familyKeys.savings(childId)` (and `familyKeys.balance(childId)`, since the earmark is released) on
 * success so the board drops the card and the Achieved shelf gains it.
 */
export function useCompleteSavingsGoal<TContext = unknown>(
  options?: UseMutationOptions<void, Error, CompleteSavingsGoalVars, TContext>,
): UseMutationResult<void, Error, CompleteSavingsGoalVars, TContext> {
  return useMutation({
    mutationFn: ({ savingsGoalId, idempotencyKey }) =>
      savingsControllerCompleteV1(savingsGoalId, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface DepositToSavingsVars {
  readonly savingsGoalId: string;
  readonly amount: number;
  /** Stable per-intent key so an offline replay never double-deposits (money/earmark move). */
  readonly idempotencyKey: string;
}

export function useDepositToSavings<TContext = unknown>(
  options?: UseMutationOptions<void, Error, DepositToSavingsVars, TContext>,
): UseMutationResult<void, Error, DepositToSavingsVars, TContext> {
  return useMutation({
    mutationFn: ({ savingsGoalId, amount, idempotencyKey }) => {
      const body: SavingsMovementDto = { amount };
      return savingsControllerDepositV1(savingsGoalId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface WithdrawFromSavingsVars {
  readonly savingsGoalId: string;
  readonly amount: number;
  /** Stable per-intent key so an offline replay never double-withdraws (earmark move back to cash). */
  readonly idempotencyKey: string;
}

/**
 * Withdraw from a savings goal (move the earmark back to spendable cash). Mirrors
 * `useDepositToSavings`: the `POST .../withdraw` route is `@Idempotent` on the backend, so the
 * `Idempotency-Key` makes an offline replay apply exactly once.
 */
export function useWithdrawFromSavings<TContext = unknown>(
  options?: UseMutationOptions<void, Error, WithdrawFromSavingsVars, TContext>,
): UseMutationResult<void, Error, WithdrawFromSavingsVars, TContext> {
  return useMutation({
    mutationFn: ({ savingsGoalId, amount, idempotencyKey }) => {
      const body: SavingsMovementDto = { amount };
      return savingsControllerWithdrawV1(savingsGoalId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

// --- habits (the kid day view + idempotent check-in / uncheck) ---

/**
 * The signed-in child's habits for today (`GET /v1/habits/today` → `TodayHabitsResponse`). The API
 * self-scopes a child caller to their OWN day view (anchored at the household-local civil day), so
 * the kid never sees a sibling's habits — there is no `childId` param on the wire. Each habit carries
 * `doneToday` + the kind streak numbers (`currentStreak`, `bestStreak`) ADR-0028 surfaces. Gated in
 * the UI on `habit:check:own`; the API enforces regardless.
 */
export function useTodayHabits(
  options?: QueryOpts<TodayHabitsResponse>,
): UseQueryResult<TodayHabitsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.habitsToday(),
    queryFn: ({ signal }) => habitsControllerTodayV1(undefined, undefined, signal),
    ...options,
  });
}

/**
 * A guardian's view of ONE child's habits for today (`GET /v1/habits/today?childId=…` → `TodayHabitsResponse`).
 * The kid's own self-scoped read is {@link useTodayHabits} (no `childId` on the wire); a guardian passes the
 * viewed child's id and the API BOLA-checks household membership before answering. Keyed by child id so a
 * guardian's per-child cache never collides with the kid's self read. Each habit carries `dueToday` +
 * `doneToday`, enough for an "N of M done today" oversight tile on the child overview.
 */
export function useChildTodayHabits(
  childId: string,
  options?: QueryOpts<TodayHabitsResponse>,
): UseQueryResult<TodayHabitsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.habitsToday(childId),
    queryFn: ({ signal }) => habitsControllerTodayV1({ childId }, undefined, signal),
    enabled: !!childId,
    ...options,
  });
}

/**
 * One habit's day-by-day history over `[from,to]` (`GET /v1/habits/:habitId/history`) — per local day
 * { date, done, excused }, gap-filled. Powers a consistency strip / calendar over an arbitrary window
 * (beyond the "recent" check-ins on the detail read). A child reads their own habit; a guardian any.
 */
export function useHabitHistory(
  habitId: string,
  from: string,
  to: string,
  options?: QueryOpts<HabitHistoryResponse>,
): UseQueryResult<HabitHistoryResponse, Error> {
  return useQuery({
    queryKey: familyKeys.habitHistory(habitId, from, to),
    queryFn: ({ signal }) => habitsControllerHistoryV1(habitId, { from, to }, undefined, signal),
    enabled: !!habitId && !!from && !!to,
    ...options,
  });
}

/** Whether a habit's `[from,to]` history is loaded and whether `day` was checked in within it. */
export interface HabitDayStatus {
  readonly habitId: string;
  readonly pending: boolean;
  readonly done: boolean;
}

/**
 * Batched sibling of {@link useHabitHistory}: one `[from,to]` history query PER habit id (via
 * `useQueries`, so the count can vary render-to-render), reduced to a simple per-habit
 * loaded/done-on-`day` status. Lets a caller that lists several habits for one day decide up-front how
 * many are actually still fillable — instead of each row resolving its own "already done" async and the
 * parent rendering a heading over what turns out to be nothing. Each query reuses the exact same key as
 * `useHabitHistory`, so the cache is shared (no double fetch).
 */
export function useHabitHistories(
  habitIds: readonly string[],
  from: string,
  to: string,
  day: string,
): HabitDayStatus[] {
  return useQueries({
    queries: habitIds.map((habitId) => ({
      queryKey: familyKeys.habitHistory(habitId, from, to),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        habitsControllerHistoryV1(habitId, { from, to }, undefined, signal),
      enabled: !!habitId && !!from && !!to,
    })),
    combine: (results) =>
      results.map((r, i) => ({
        habitId: habitIds[i] as string,
        pending: r.isPending,
        done: r.data?.points.some((p) => p.date === day && p.done) ?? false,
      })),
  });
}

export interface HabitCheckVars {
  readonly habitId: string;
  /** Stable per-intent key so an offline replay never double-writes the day's check-in. */
  readonly idempotencyKey: string;
  /**
   * Optional `YYYY-MM-DD` to BACK-DATE the check-in (or, via `useUncheckHabit`, the undo) to a past due
   * day within the bounded window (ADR-0040). Omit for today.
   */
  readonly localDate?: string;
}

/**
 * Check a habit in for today (`POST /v1/habits/:habitId/check-in` → `CheckInHabitResponse`). The
 * route is `@Idempotent`, so we carry an `Idempotency-Key` and an offline replay is recorded exactly
 * once (a replay returns `recorded: false`). The streak is append-only/forgiving (ADR-0028) — a
 * check-in never deducts anything; it only ever grows the "days shown up" count. Mirrors
 * `useCompleteTask`: the consuming feature owns the optimistic cache surgery + rollback.
 */
export function useCheckInHabit<TContext = unknown>(
  options?: UseMutationOptions<CheckInHabitResponse, Error, HabitCheckVars, TContext>,
): UseMutationResult<CheckInHabitResponse, Error, HabitCheckVars, TContext> {
  return useMutation({
    mutationFn: ({ habitId, idempotencyKey, localDate }) => {
      const body: CheckInHabitDto = localDate === undefined ? {} : { localDate };
      return habitsControllerCheckInV1(habitId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

/**
 * Undo a check-in (`POST /v1/habits/:habitId/uncheck` → `UncheckHabitResponse`) — a kind affordance
 * for a mis-tap, never a punishment. Same-day by default; an optional `localDate` undoes a past day's
 * check-in instead (mirrors `useCheckInHabit`'s back-date). `@Idempotent` on the backend, so the
 * `Idempotency-Key` makes an offline replay apply exactly once (`removed: false` when nothing was
 * checked in).
 */
export function useUncheckHabit<TContext = unknown>(
  options?: UseMutationOptions<UncheckHabitResponse, Error, HabitCheckVars, TContext>,
): UseMutationResult<UncheckHabitResponse, Error, HabitCheckVars, TContext> {
  return useMutation({
    mutationFn: ({ habitId, idempotencyKey, localDate }) => {
      const body: UncheckHabitDto = localDate === undefined ? {} : { localDate };
      return habitsControllerUncheckV1(habitId, body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface HabitExcuseVars {
  readonly habitId: string;
  /** The past due day to excuse / unexcuse (`YYYY-MM-DD`). */
  readonly date: string;
  /** Fresh per-intent key — the route is `@Idempotent`, so without it the server 400s. */
  readonly idempotencyKey: string;
}

/**
 * Mark a past due day a "rest day" (`POST /v1/habits/:habitId/excuse` → `ExcuseHabitDayResponse`). An
 * excused day flows through the streak like a non-due day — it neither counts nor breaks the run (ADR-
 * 0040), no points, no shame. The route is `@Idempotent`, so we MUST carry an `Idempotency-Key` (the raw
 * generated hook omits it and 400s); a fresh key per intent also makes an offline replay a no-op.
 */
export function useExcuseHabit<TContext = unknown>(
  options?: UseMutationOptions<ExcuseHabitDayResponse, Error, HabitExcuseVars, TContext>,
): UseMutationResult<ExcuseHabitDayResponse, Error, HabitExcuseVars, TContext> {
  return useMutation({
    mutationFn: ({ habitId, date, idempotencyKey }) =>
      habitsControllerExcuseV1(habitId, { date }, idempotent(idempotencyKey)),
    ...options,
  });
}

/**
 * Remove an excuse (`DELETE /v1/habits/:habitId/excuse/:date` → `UnexcuseHabitDayResponse`): the day
 * becomes a normal due day again. Also `@Idempotent`, so it carries a fresh `Idempotency-Key`.
 */
export function useUnexcuseHabit<TContext = unknown>(
  options?: UseMutationOptions<UnexcuseHabitDayResponse, Error, HabitExcuseVars, TContext>,
): UseMutationResult<UnexcuseHabitDayResponse, Error, HabitExcuseVars, TContext> {
  return useMutation({
    mutationFn: ({ habitId, date, idempotencyKey }) =>
      habitsControllerUnexcuseV1(habitId, date, idempotent(idempotencyKey)),
    ...options,
  });
}

// --- play / progression (ADR-0029: status XP/level + a companion that reflects, never punishes) ---

/**
 * A child's progression snapshot (`GET /v1/play/state` → `PlayStateResponse`): the lifetime,
 * non-spendable `totalXp` + derived `level` (status, kept VISUALLY DISTINCT from the spendable coin
 * balance), the XP into/needed for the current level band, and a `companionStage` (the monotonic
 * growth-form high-water mark — 0..5 for the legacy-plant companion an unchosen child sees) + gentle
 * `companionMood` ('thriving' | 'resting') that grow from recent consistency. `childId` is the
 * guardian path (`?childId=` on the wire — a guardian may view any child of the household); pass
 * `null` for the self view (the API defaults to the caller, and BOLA restricts a child to their own
 * state). Status never decreases and the companion never shames (no sad/punitive state), so this is
 * a read-only, secondary surface the screen never blocks on.
 */
export function usePlayState(
  childId: string | null,
  options?: QueryOpts<PlayStateResponse>,
): UseQueryResult<PlayStateResponse, Error> {
  return useQuery({
    queryKey: familyKeys.playState(childId),
    queryFn: ({ signal }) =>
      playControllerStateV1(childId ? { childId } : undefined, undefined, signal),
    ...options,
  });
}

// --- cosmetics (ADR-0029: cosmetic-only coin sink — level unlocks, coins buy, no pay-to-win) ---

/**
 * A child's cosmetics catalog (`GET /v1/cosmetics/catalog` → `CosmeticCatalogResponse`): the current
 * coin balance + play level (advisory) and every catalog item with per-child `owned` / `equipped` /
 * `affordable` / `unlocked` flags + `cost` / `unlockLevel`. `childId` is the guardian view path
 * (`?childId=` on the wire); pass `null` for the self view (the API defaults to the caller, and BOLA
 * restricts a child to their own catalog — a kid shops with their OWN coins). Buying is gated in the
 * UI on `cosmetic:purchase`; the API enforces regardless. Cosmetics are STATUS-neutral: buying spends
 * coins (the sink) and never touches XP/level.
 */
export function useCosmeticsCatalog(
  childId: string | null,
  options?: QueryOpts<CosmeticCatalogResponse>,
): UseQueryResult<CosmeticCatalogResponse, Error> {
  return useQuery({
    queryKey: familyKeys.cosmeticsCatalog(childId),
    queryFn: ({ signal }) =>
      cosmeticsControllerCatalogV1(childId ? { childId } : undefined, undefined, signal),
    ...options,
  });
}

/**
 * A child's currently-equipped cosmetics (`GET /v1/cosmetics/equipped` →
 * `EquippedCosmeticsResponse`): at most one item per wear-region CATEGORY (`outfit` | `headwear` |
 * `face` | `back` | `background`, ADR-0098). `childId` is the guardian view path (`?childId=`); pass
 * `null` for the self view. Read by the kid hero/avatar surfaces so the equipped look themes the
 * visuals; the default look is used while loading / when nothing is equipped.
 */
export function useEquippedCosmetics(
  childId: string | null,
  options?: QueryOpts<EquippedCosmeticsResponse>,
): UseQueryResult<EquippedCosmeticsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.cosmeticsEquipped(childId),
    queryFn: ({ signal }) =>
      cosmeticsControllerEquippedV1(childId ? { childId } : undefined, undefined, signal),
    ...options,
  });
}

export interface PurchaseCosmeticVars {
  readonly itemId: string;
  /**
   * Stable per-intent key so an offline replay (PWA outbox) never double-debits coins. The route is
   * `@Idempotent` on the backend; the server dedupes the replay (a replay returns `purchased: false`).
   */
  readonly idempotencyKey: string;
}

/**
 * Buy a cosmetic (`POST /v1/cosmetics/purchase` → `PurchaseCosmeticResponse`). Carries an
 * `Idempotency-Key` (the route is `@Idempotent`), so an offline replay applies exactly once — the
 * spend is the coin sink (ADR-0029). The consuming feature invalidates the catalog + equipped + the
 * child's coin balance on settle and surfaces locked/over-level + insufficient-funds errors kindly.
 */
export function usePurchaseCosmetic<TContext = unknown>(
  options?: UseMutationOptions<PurchaseCosmeticResponse, Error, PurchaseCosmeticVars, TContext>,
): UseMutationResult<PurchaseCosmeticResponse, Error, PurchaseCosmeticVars, TContext> {
  return useMutation({
    mutationFn: ({ itemId, idempotencyKey }) => {
      const body: CosmeticItemDto = { itemId };
      return cosmeticsControllerPurchaseV1(body, idempotent(idempotencyKey));
    },
    ...options,
  });
}

export interface ChooseHeroVars {
  readonly data: ChooseHeroDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the choose exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Choose (or re-choose) a hero (`POST /v1/play/hero` → `ChooseHeroResponse`, ADR-0095 §2). Carries an
 * `Idempotency-Key` (the route is `@Idempotent`); re-choosing the SAME archetype is a server no-op, so a
 * double-submit / offline replay never re-fires the onboarding celebration.
 */
export function useChooseHero<TContext = unknown>(
  options?: UseMutationOptions<ChooseHeroResponse, Error, ChooseHeroVars, TContext>,
): UseMutationResult<ChooseHeroResponse, Error, ChooseHeroVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerChooseHeroV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface UnlockHeroVars {
  readonly data: UnlockHeroDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the unlock exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Unlock a new hero by spending effort-stars (✨) (`POST /v1/play/hero/unlock` → `UnlockHeroResponse`,
 * ADR-0101). Carries an `Idempotency-Key` (the route is `@Idempotent`); unlocking an already-owned hero is
 * a server no-op (`unlocked: false`), and the ✨ debit is keyed per-archetype, so a double-submit / offline
 * replay never double-charges. Unlocking only ADDS the hero to the collection — the kid then chooses it.
 */
export function useUnlockHero<TContext = unknown>(
  options?: UseMutationOptions<UnlockHeroResponse, Error, UnlockHeroVars, TContext>,
): UseMutationResult<UnlockHeroResponse, Error, UnlockHeroVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerUnlockHeroV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface UnlockPetVars {
  readonly data: UnlockPetDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the unlock exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Unlock a cross-hero pet by spending effort-stars (✨) (`POST /v1/play/pet/unlock` → `UnlockPetResponse`,
 * ADR-0108). Carries an `Idempotency-Key` (the route is `@Idempotent`); unlocking an already-owned pet is a
 * server no-op (`unlocked: false`), and the ✨ debit is keyed per-pet, so a double-submit / offline replay
 * never double-charges. Unlocking only ADDS the pet to the collection — the kid then equips it.
 */
export function useUnlockPet<TContext = unknown>(
  options?: UseMutationOptions<UnlockPetResponse, Error, UnlockPetVars, TContext>,
): UseMutationResult<UnlockPetResponse, Error, UnlockPetVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerUnlockPetV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface EquipPetVars {
  readonly data: EquipPetDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the equip exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Equip an owned pet beside the active hero, or take it off (`POST /v1/play/pet` → `EquipPetResponse`,
 * ADR-0108). `data.petId` is the pet slug, or `null` to clear the slot. `@Idempotent`; re-equipping the same
 * pet is a server no-op. The pet follows whichever hero is chosen (cross-hero).
 */
export function useEquipPet<TContext = unknown>(
  options?: UseMutationOptions<EquipPetResponse, Error, EquipPetVars, TContext>,
): UseMutationResult<EquipPetResponse, Error, EquipPetVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerEquipPetV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface UnlockBackgroundVars {
  readonly data: UnlockBackgroundDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the unlock exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Unlock a cross-hero background by spending effort-stars (✨) (`POST /v1/play/background/unlock` →
 * `UnlockBackgroundResponse`, ADR-0110 §7 · ADR-0111). Carries an `Idempotency-Key` (the route is
 * `@Idempotent`); unlocking an already-owned background is a server no-op, so a replay never double-charges.
 * Unlocking only ADDS the background to the collection — the kid then equips it beside any hero.
 */
export function useUnlockBackground<TContext = unknown>(
  options?: UseMutationOptions<UnlockBackgroundResponse, Error, UnlockBackgroundVars, TContext>,
): UseMutationResult<UnlockBackgroundResponse, Error, UnlockBackgroundVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerUnlockBackgroundV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface EquipBackgroundVars {
  readonly data: EquipBackgroundDto;
  /** Per-intent key so an offline replay (PWA outbox) re-applies the equip exactly once. */
  readonly idempotencyKey: string;
}

/**
 * Equip an owned background beside the active hero, or clear it (`POST /v1/play/background` →
 * `EquipBackgroundResponse`, ADR-0110 §7). `data.backgroundId` is the slug, or `null` to clear the slot.
 * `@Idempotent`; the backdrop follows whichever hero is chosen (cross-hero).
 */
export function useEquipBackground<TContext = unknown>(
  options?: UseMutationOptions<EquipBackgroundResponse, Error, EquipBackgroundVars, TContext>,
): UseMutationResult<EquipBackgroundResponse, Error, EquipBackgroundVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerEquipBackgroundV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface RenameHeroVars {
  readonly data: RenameHeroDto;
  readonly idempotencyKey: string;
}

/**
 * Rename the already-chosen hero (`PATCH /v1/play/hero/name` → `RenameHeroResponse`, ADR-0095 §2).
 * `@Idempotent`, so an offline replay applies exactly once.
 */
export function useRenameHero<TContext = unknown>(
  options?: UseMutationOptions<RenameHeroResponse, Error, RenameHeroVars, TContext>,
): UseMutationResult<RenameHeroResponse, Error, RenameHeroVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      playControllerRenameHeroV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface EquipCosmeticVars {
  readonly itemId: string;
}

/**
 * Equip an owned cosmetic (`POST /v1/cosmetics/equip`). One active item per slot — equipping a new
 * item swaps out the previous one in that slot (the server handles the swap). NOT a money move (no
 * coins change hands), so no idempotency key; the consuming feature invalidates catalog + equipped.
 */
export function useEquipCosmetic<TContext = unknown>(
  options?: UseMutationOptions<void, Error, EquipCosmeticVars, TContext>,
): UseMutationResult<void, Error, EquipCosmeticVars, TContext> {
  return useMutation({
    mutationFn: ({ itemId }) => {
      const body: CosmeticItemDto = { itemId };
      return cosmeticsControllerEquipV1(body);
    },
    ...options,
  });
}

/**
 * Unequip the active cosmetic in an item's slot (`POST /v1/cosmetics/unequip`) — back to the default
 * look for that slot. Mirrors {@link useEquipCosmetic}: not a money move, so no idempotency key.
 */
export function useUnequipCosmetic<TContext = unknown>(
  options?: UseMutationOptions<void, Error, EquipCosmeticVars, TContext>,
): UseMutationResult<void, Error, EquipCosmeticVars, TContext> {
  return useMutation({
    mutationFn: ({ itemId }) => {
      const body: CosmeticItemDto = { itemId };
      return cosmeticsControllerUnequipV1(body);
    },
    ...options,
  });
}

// --- cooperative family quests (ADR-0029: additive shared goals — cooperative, never ranked) ---

/**
 * The household's CURRENT cooperative quest(s) (`GET /v1/quests/active` → `{ quests: QuestResponse[] }`):
 * every `active` quest PLUS recently-`completed` ones — a quest auto-completes the instant the bar
 * fills, so the just-completed quest stays in this read (until archived / the celebration window
 * lapses) and the shared card can show the family its "You did it together!" moment.
 * EVERYONE in the household reads the SAME shared card (`household:read`, which even a child holds) —
 * the quest is cooperative, so a kid and a guardian see identical shared progress + contributors. The
 * shared bar moves as kids complete chores/habits, so consuming surfaces invalidate
 * {@link familyKeys.questsActive} on a chore-complete / habit-check-in / chore-approval (or simply
 * refetch on focus). No params on the wire — the access token scopes the household.
 */
export function useActiveQuests(
  options?: QueryOpts<ActiveQuestsResponse>,
): UseQueryResult<ActiveQuestsResponse, Error> {
  return useQuery({
    queryKey: familyKeys.questsActive(),
    queryFn: ({ signal }) => questsControllerActiveV1(undefined, signal),
    ...options,
  });
}

/**
 * The household's quest history (`GET /v1/quests` → cursor `Page<QuestResponse>`): active + completed
 * + archived quests, newest first. Drives the guardian management list. Cursor-paginated (the server
 * caps a page at its default limit, 20): pass the previous page's `nextCursor` to fetch the next one —
 * a long-lived household outgrows the first page, so the surface renders a "show more" pager off
 * `hasMore`/`nextCursor` (each page caches under its own key slot, like the ledger). Gated in the UI
 * on `quest:manage`; the API enforces regardless.
 */
export function useQuestHistory(
  cursor: string | null = null,
  options?: QueryOpts<QuestsControllerListV1200>,
): UseQueryResult<QuestsControllerListV1200, Error> {
  return useQuery({
    queryKey: [...familyKeys.questsHistory(), cursor ?? 'first'],
    queryFn: ({ signal }) =>
      questsControllerListV1(cursor ? { cursor } : undefined, undefined, signal),
    ...options,
  });
}

export interface QuestObjectiveVars {
  readonly questId: string;
  readonly objectiveId: string;
  /** Carried as the `Idempotency-Key` (the route is `@Idempotent`) so an offline replay never double-counts. */
  readonly idempotencyKey: string;
}

/**
 * Tick a `milestone` objective complete (`POST .../objectives/:id/complete`, `quest:manage`). The route
 * is `@Idempotent`, so we MUST carry an `Idempotency-Key` — without it the server 400s (the raw generated
 * hook sends none). Invalidates the active card + history so the shared bar reconciles.
 */
export function useCompleteQuestObjective<TContext = unknown>(
  options?: UseMutationOptions<void, Error, QuestObjectiveVars, TContext>,
): UseMutationResult<void, Error, QuestObjectiveVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questId, objectiveId, idempotencyKey }) =>
      questsControllerCompleteObjectiveV1(questId, objectiveId, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      void qc.invalidateQueries({ queryKey: familyKeys.questsHistory() });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * "Everyone pitches in" (ADR-0049): add +1 to a `together` objective (`POST .../objectives/:id/pitch-in`,
 * `household:read` — any member). The route is `@Idempotent`, so we carry a FRESH `Idempotency-Key` per
 * tap (distinct taps each count; an offline replay of one tap never double-counts). Invalidates the views.
 */
export function usePitchInQuest<TContext = unknown>(
  options?: UseMutationOptions<void, Error, QuestObjectiveVars, TContext>,
): UseMutationResult<void, Error, QuestObjectiveVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questId, objectiveId, idempotencyKey }) =>
      questsControllerPitchInV1(questId, objectiveId, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      void qc.invalidateQueries({ queryKey: familyKeys.questsHistory() });
      options?.onSuccess?.(...args);
    },
  });
}

export interface CreateQuestVars {
  readonly data: CreateQuestDto;
  /** Stable per-intent key so an offline replay / retried timeout never creates a duplicate quest. */
  readonly idempotencyKey: string;
}

/**
 * Create a cooperative family quest (`POST /v1/quests` → `{ questId }`, `quest:manage`). Maps 1:1 to
 * `CreateQuestDto`: title (1–80, USER content — never translated), optional description, the shared
 * `targetCount` (≥1), `countsKind` (chore | habit | both), the household-local `startDate` + optional
 * `endDate`, and an optional `rewardNote` (USER content — the IRL celebration the family honors; no
 * auto-payout). Carries an `Idempotency-Key` (the route is `@Idempotent`): an offline-queued or
 * retried create that already landed replays the SAME quest instead of minting a duplicate the
 * projection would double-count. On success the active card + the history list are invalidated so
 * both reconcile.
 */
export function useCreateQuest<TContext = unknown>(
  options?: UseMutationOptions<CreateQuestResponse, Error, CreateQuestVars, TContext>,
): UseMutationResult<CreateQuestResponse, Error, CreateQuestVars, TContext> {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }) =>
      questsControllerCreateV1(data, idempotent(idempotencyKey)),
    ...options,
  });
}

export interface ArchiveQuestVars {
  readonly questId: string;
  /**
   * Carried as the `Idempotency-Key` header so an offline replay / retried timeout can never be
   * silently dropped — `apiFetch`'s offline-outbox bridge only queues a failed write when it can
   * read this header off the request. Archiving is domain-idempotent (re-archiving an
   * already-archived quest is a no-op), so a replay is always safe.
   */
  readonly idempotencyKey: string;
}

/**
 * Archive a family quest (`POST /v1/quests/:questId/archive`, 204, `quest:manage`). Archiving wraps a
 * quest up early — it stops appearing as the active shared card; the progress + contributors stay in
 * history (never deleted — kind). A quest never held points, so archiving is purely a state change
 * (no compensating ledger entry). Carries an `Idempotency-Key` so an offline-queued or retried archive
 * replays instead of being silently lost. The consuming surface invalidates the active card + the history.
 */
export function useArchiveQuest<TContext = unknown>(
  options?: UseMutationOptions<void, Error, ArchiveQuestVars, TContext>,
): UseMutationResult<void, Error, ArchiveQuestVars, TContext> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questId, idempotencyKey }) =>
      questsControllerArchiveV1(questId, idempotent(idempotencyKey)),
    ...options,
    onSuccess: (...args) => {
      // Archiving a (typically just-completed) quest removes it from the shared active card and updates
      // history — invalidate both so the celebration card clears once a guardian wraps it up.
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      void qc.invalidateQueries({ queryKey: familyKeys.questsHistory() });
      options?.onSuccess?.(...args);
    },
  });
}

/** Fields that can be patched after a quest is created (objectives/rule/startDate are immutable). */
export interface UpdateQuestMetaDto {
  readonly title?: string;
  readonly description?: string | null;
  readonly questType?: string;
  readonly endDate?: string | null;
  readonly rewardNote?: string | null;
}

export interface UpdateQuestVars {
  readonly questId: string;
  readonly data: UpdateQuestMetaDto;
  /**
   * Carried as the `Idempotency-Key` header (mirrors `useCreateQuest`) so an offline replay /
   * retried timeout can never be silently dropped — `apiFetch`'s offline-outbox bridge only queues
   * a failed write when it can read this header off the request. The update is a full field
   * overwrite (undefined = "leave as-is"), so a replay is always safe.
   */
  readonly idempotencyKey: string;
}

/**
 * Update cosmetic / scheduling metadata on a non-archived quest (`PATCH /v1/quests/:questId`, 204,
 * `quest:manage`). Only the supplied fields change — undefined = "leave as-is". Objectives, the
 * completion rule, and the start date remain immutable (progress is additive-only and must not be
 * retroactively reframed). Carries an `Idempotency-Key` so an offline-queued or retried edit replays
 * instead of being silently lost. On success the active card + the history list are invalidated.
 */
export function useUpdateQuest<TContext = unknown>(
  options?: UseMutationOptions<void, Error, UpdateQuestVars, TContext>,
): UseMutationResult<void, Error, UpdateQuestVars, TContext> {
  return useMutation({
    mutationFn: ({ questId, data, idempotencyKey }) =>
      apiFetch<void>({
        url: `/v1/quests/${questId}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        data,
      }),
    ...options,
  });
}
